import { createContext, useContext, useEffect, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingRegion } from "@/components/ui/loading";
import { QueryFailure } from "@/components/StudioPrimitives";
import { Button } from "@/components/ui/button";
import type { MeOrg, OrgRole } from "@/lib/auth/types";
import { rememberWorkspace } from "./lastWorkspace";
import {
  createWorkspaceLinks,
  DIRECTORY_PATH,
  type WorkspaceLinks,
} from "./paths";
import { dropForeignWorkspaceQueries, useWorkspaces } from "./queries";

export interface ActiveWorkspace {
  workspace: MeOrg;
  slug: string;
  role: OrgRole;
  isOwner: boolean;
  links: WorkspaceLinks;
}

const ActiveWorkspaceContext = createContext<ActiveWorkspace | null>(null);

export function WorkspaceLayout() {
  const { workspaceSlug = "" } = useParams();
  const queryClient = useQueryClient();
  const organizations = useWorkspaces();
  const workspace = organizations.data?.find(
    (item) => item.slug === workspaceSlug,
  );
  const value = useMemo<ActiveWorkspace | null>(() => {
    if (!workspace) return null;
    return {
      workspace,
      slug: workspace.slug,
      role: workspace.role,
      isOwner: workspace.role === "owner",
      links: createWorkspaceLinks(workspace.slug),
    };
  }, [workspace]);

  useEffect(() => {
    if (value) rememberWorkspace(value.slug);
  }, [value]);

  // Keyed on the slug, not on the membership list: `useWorkspaces` rebuilds
  // its array on every session revalidation, so depending on that identity
  // would sweep the whole cache on a timer instead of on a real switch.
  useEffect(() => {
    dropForeignWorkspaceQueries(queryClient, value?.slug ?? "");
  }, [queryClient, value?.slug]);

  if (organizations.isPending) {
    return <LoadingRegion label="Loading organization…" />;
  }
  if (organizations.isError) {
    return (
      <QueryFailure
        error={organizations.error}
        retry={() => void organizations.refetch()}
        title="Your organizations could not be loaded."
      />
    );
  }
  if (!value) return <OrganizationUnavailable />;

  return (
    <ActiveWorkspaceContext.Provider value={value}>
      <Outlet />
    </ActiveWorkspaceContext.Provider>
  );
}

export function useActiveWorkspace(): ActiveWorkspace {
  const value = useContext(ActiveWorkspaceContext);
  if (!value) {
    throw new Error("useActiveWorkspace must be used inside WorkspaceLayout");
  }
  return value;
}

/**
 * Pathless layout for every route that reads organization content.
 *
 * A member of an organization without its own Characters workspace would get
 * `characters_workspace_not_provisioned` from every content request, so the
 * screens under this guard never mount for it and never send one. Team
 * settings sit outside the guard because membership still works.
 */
export function RequireCharactersWorkspace() {
  const { workspace, links } = useActiveWorkspace();
  if (workspace.characters_workspace) return <Outlet />;
  return (
    <section className="space-y-4 px-4 py-16 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight">
        Characters isn't set up for {workspace.display_name} yet.
      </h1>
      <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
        A platform operator provisions each organization's own Characters
        workspace. Team settings are available in the meantime.
      </p>
      <Button asChild variant="outline">
        <Link to={links.team}>Open Team settings</Link>
      </Button>
    </section>
  );
}

function OrganizationUnavailable() {
  return (
    <section className="space-y-4 px-4 py-16 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight">
        This organization is not available to this account.
      </h1>
      <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
        Choose an organization from your existing memberships.
      </p>
      <Button asChild variant="outline">
        <Link to={DIRECTORY_PATH}>Choose organization</Link>
      </Button>
    </section>
  );
}
